import { action, observable, makeObservable } from 'mobx';
import { Model, IModelAttributes, attribute, entity } from 'Models/Model';
import * as Models from 'Models/Entities';

export interface IStopEntityAttributes extends IModelAttributes {
	order: number;

	endAlterations: Array<Models.AlterationEntity | Models.IAlterationEntityAttributes>;
	endSegments: Array<Models.SegmentEntity | Models.ISegmentEntityAttributes>;
	endStopPrices: Array<Models.PriceEntity | Models.IPriceEntityAttributes>;
	startAlterations: Array<Models.AlterationEntity | Models.IAlterationEntityAttributes>;
	startSegments: Array<Models.SegmentEntity | Models.ISegmentEntityAttributes>;
	startStopPrices: Array<Models.PriceEntity | Models.IPriceEntityAttributes>;
	locationId: string;
	location: Models.LocationEntity | Models.ILocationEntityAttributes;
	routeId: string;
	route: Models.RouteEntity | Models.IRouteEntityAttributes;
	tripStops: Array<Models.TripStopEntity | Models.ITripStopEntityAttributes>;
	checkInStatusHistories: Array<Models.CheckInStatusHistoryEntity | Models.ICheckInStatusHistoryEntityAttributes>;
}
@entity('StopEntity', 'Stop')
export default class StopEntity extends Model implements IStopEntityAttributes {
	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	@observable
	@attribute<StopEntity, number>()
	public order: number;

	@observable
	@attribute({ isReference: true, manyReference: true })
	public endAlterations: Models.AlterationEntity[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	public endSegments: Models.SegmentEntity[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	public endStopPrices: Models.PriceEntity[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	public startAlterations: Models.AlterationEntity[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	public startSegments: Models.SegmentEntity[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	public startStopPrices: Models.PriceEntity[] = [];

	@observable
	@attribute()
	public locationId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public location: Models.LocationEntity;

	@observable
	@attribute()
	public routeId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public route: Models.RouteEntity;

	@observable
	@attribute({ isReference: true, manyReference: true })
	public tripStops: Models.TripStopEntity[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	public checkInStatusHistories: Models.CheckInStatusHistoryEntity[] = [];

	constructor(attributes?: Partial<IStopEntityAttributes>) {
		super(attributes);

		this.endAlterations = [];
		this.endSegments = [];
		this.endStopPrices = [];
		this.startAlterations = [];
		this.startSegments = [];
		this.startStopPrices = [];
		this.tripStops = [];
		this.checkInStatusHistories = [];

		makeObservable(this);

		this.assignAttributes(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	public assignAttributes(attributes?: Partial<IStopEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.order !== undefined) {
				this.order = attributes.order;
			}
			if (attributes.endAlterations !== undefined && Array.isArray(attributes.endAlterations)) {
				if (!this.endAlterations) {
					this.endAlterations = [];
				}
				for (const model of attributes.endAlterations) {
					if (model instanceof Models.AlterationEntity) {
						this.endAlterations.push(model);
					} else {
						this.endAlterations.push(new Models.AlterationEntity(model));
					}
				}
			}
			if (attributes.endSegments !== undefined && Array.isArray(attributes.endSegments)) {
				if (!this.endSegments) {
					this.endSegments = [];
				}
				for (const model of attributes.endSegments) {
					if (model instanceof Models.SegmentEntity) {
						this.endSegments.push(model);
					} else {
						this.endSegments.push(new Models.SegmentEntity(model));
					}
				}
			}
			if (attributes.endStopPrices !== undefined && Array.isArray(attributes.endStopPrices)) {
				if (!this.endStopPrices) {
					this.endStopPrices = [];
				}
				for (const model of attributes.endStopPrices) {
					if (model instanceof Models.PriceEntity) {
						this.endStopPrices.push(model);
					} else {
						this.endStopPrices.push(new Models.PriceEntity(model));
					}
				}
			}
			if (attributes.startAlterations !== undefined && Array.isArray(attributes.startAlterations)) {
				if (!this.startAlterations) {
					this.startAlterations = [];
				}
				for (const model of attributes.startAlterations) {
					if (model instanceof Models.AlterationEntity) {
						this.startAlterations.push(model);
					} else {
						this.startAlterations.push(new Models.AlterationEntity(model));
					}
				}
			}
			if (attributes.startSegments !== undefined && Array.isArray(attributes.startSegments)) {
				if (!this.startSegments) {
					this.startSegments = [];
				}
				for (const model of attributes.startSegments) {
					if (model instanceof Models.SegmentEntity) {
						this.startSegments.push(model);
					} else {
						this.startSegments.push(new Models.SegmentEntity(model));
					}
				}
			}
			if (attributes.startStopPrices !== undefined && Array.isArray(attributes.startStopPrices)) {
				if (!this.startStopPrices) {
					this.startStopPrices = [];
				}
				for (const model of attributes.startStopPrices) {
					if (model instanceof Models.PriceEntity) {
						this.startStopPrices.push(model);
					} else {
						this.startStopPrices.push(new Models.PriceEntity(model));
					}
				}
			}
			if (attributes.locationId !== undefined) {
				this.locationId = attributes.locationId;
			}
			if (attributes.location !== undefined) {
				if (attributes.location === null) {
					this.location = attributes.location;
				} else if (attributes.location instanceof Models.LocationEntity) {
					this.location = attributes.location;
					this.locationId = attributes.location.id;
				} else {
					this.location = new Models.LocationEntity(attributes.location);
					this.locationId = this.location.id;
				}
			}
			if (attributes.routeId !== undefined) {
				this.routeId = attributes.routeId;
			}
			if (attributes.route !== undefined) {
				if (attributes.route === null) {
					this.route = attributes.route;
				} else if (attributes.route instanceof Models.RouteEntity) {
					this.route = attributes.route;
					this.routeId = attributes.route.id;
				} else {
					this.route = new Models.RouteEntity(attributes.route);
					this.routeId = this.route.id;
				}
			}
			if (attributes.tripStops !== undefined && Array.isArray(attributes.tripStops)) {
				if (!this.tripStops) {
					this.tripStops = [];
				}
				for (const model of attributes.tripStops) {
					if (model instanceof Models.TripStopEntity) {
						this.tripStops.push(model);
					} else {
						this.tripStops.push(new Models.TripStopEntity(model));
					}
				}
			}
			if (attributes.checkInStatusHistories !== undefined && Array.isArray(attributes.checkInStatusHistories)) {
				if (!this.checkInStatusHistories) {
					this.checkInStatusHistories = [];
				}
				for (const model of attributes.checkInStatusHistories) {
					if (model instanceof Models.CheckInStatusHistoryEntity) {
						this.checkInStatusHistories.push(model);
					} else {
						this.checkInStatusHistories.push(new Models.CheckInStatusHistoryEntity(model));
					}
				}
			}
		}
	}
}
